import { generateNanoId } from "@/shared";
import {
  type ITableCell,
  type ITableListDynamicVarData,
  type ITableSettings,
  TableHeaderPosition,
} from "../types";
import { tableNormalizeColWidths } from "./table-normalize-col-widths";

interface IProps {
  settings: ITableSettings;
  data: ITableListDynamicVarData;
}

const escapeData = (data: Record<string, string>) => {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      typeof value === "string"
        ? value
            .replace(/"/g, '\\"')
            .replace(/[\r\n]+/g, " ")
            .replace(/\s+/g, " ")
            .trim()
        : value,
    ]),
  );
};

const getFinalContent = (data: Record<string, string>, cell: ITableCell) => {
  return Object.entries(data).reduce(
    (str, [key, value]) => str.replace(new RegExp(`\\[${key}\\]`, "g"), value),
    cell.content,
  );
};

export const parseTableListDynamicVar = ({
  settings,
  data,
}: IProps): ITableSettings => {
  if (!data || data.data.length === 0) {
    return settings;
  }

  const { tableHasHeader, tableHeaderPosition } = settings;

  let tableCols = settings.tableCols;
  let tableRows = settings.tableRows;
  let tableColWidths = settings.tableColWidths;
  let tableRowHeights = settings.tableRowHeights;
  const tableData = [...settings.tableData];

  if (tableHasHeader) {
    if (tableHeaderPosition === TableHeaderPosition.Top) {
      tableRowHeights = tableRowHeights.slice(0, 1);
      tableRows = 1;
      const templateCells = tableData.splice(tableCols, tableCols);

      for (const row of data.data) {
        tableRows++;

        const replaceData = escapeData(row);
        for (const cell of templateCells) {
          tableData.push({
            ...cell,
            id: generateNanoId(),
            content: getFinalContent(replaceData, cell),
          });
        }
      }
    } else if (tableHeaderPosition === TableHeaderPosition.Bottom) {
      tableRows = 1;
      const templateCells = tableData.splice(0, tableCols);

      const newData: ITableCell[] = [];
      for (const row of data.data) {
        tableRows++;
        const replaceData = escapeData(row);

        const newCells: ITableCell[] = [];
        for (const cell of templateCells) {
          newCells.push({
            ...cell,
            id: generateNanoId(),
            content: getFinalContent(replaceData, cell),
          });
        }
        newData.unshift(...newCells);
      }

      tableData.unshift(...newData);
      const lastHeight = tableRowHeights[tableRowHeights.length - 1];
      tableRowHeights = [];
      tableRowHeights[tableRows - 1] = lastHeight;
    } else if (tableHeaderPosition === TableHeaderPosition.Left) {
      const templateCellsIdxs = Array.from(
        { length: tableRows },
        (_, idx) => 1 + idx * tableCols,
      );
      tableCols = 1;
      const templateCells: ITableCell[] = [];
      for (let i = templateCellsIdxs.length - 1; i >= 0; i--) {
        const cell = tableData.splice(templateCellsIdxs[i], 1)[0];
        templateCells.unshift(cell);
      }

      for (const col of data.data) {
        tableCols++;
        const replaceData = escapeData(col);

        for (let cellIdx = 0; cellIdx < templateCells.length; cellIdx++) {
          const cell = templateCells[cellIdx];
          tableData.splice(tableCols - 1 + cellIdx * tableCols, 0, {
            ...cell,
            id: generateNanoId(),
            content: getFinalContent(replaceData, cell),
          });
        }
      }

      tableColWidths = tableColWidths.slice(0, 1);
    } else if (tableHeaderPosition === TableHeaderPosition.Right) {
      const templateCellsIdxs = Array.from(
        { length: tableRows },
        (_, idx) => tableCols - 2 + idx * tableCols,
      );
      tableCols = 1;
      const templateCells: ITableCell[] = [];
      for (let i = templateCellsIdxs.length - 1; i >= 0; i--) {
        const cell = tableData.splice(templateCellsIdxs[i], 1)[0];
        templateCells.unshift(cell);
      }

      for (const col of data.data) {
        const replaceData = escapeData(col);

        tableCols++;
        for (let cellIdx = 0; cellIdx < templateCells.length; cellIdx++) {
          const cell = templateCells[cellIdx];
          tableData.splice(cellIdx * tableCols, 0, {
            ...cell,
            id: generateNanoId(),
            content: getFinalContent(replaceData, cell),
          });
        }
      }

      tableColWidths = tableColWidths.slice(0, 1);
    }
  } else {
    tableRowHeights = [];
    tableRows = 0;
    const templateCells = tableData.splice(0, tableCols);

    for (const row of data.data) {
      tableRows++;
      const replaceData = escapeData(row);

      for (const cell of templateCells) {
        tableData.push({
          ...cell,
          id: generateNanoId(),
          content: getFinalContent(replaceData, cell),
        });
      }
    }
  }

  if (tableColWidths.length !== tableCols) {
    const amtOfNewWidths = tableCols - tableColWidths.length;
    const newWidth = 100 / tableCols;

    const newWidths = tableColWidths.slice(0, tableCols);
    for (let i = 0; i < amtOfNewWidths; i++) {
      newWidths.push(newWidth);
    }

    tableColWidths = tableNormalizeColWidths(newWidths);
  }

  return {
    ...settings,
    tableData,
    tableCols,
    tableRows,
    tableColWidths,
    tableRowHeights,
  };
};
